import React from 'react';
import { inlineChangeSpans, paperUnderlineMap, splitUnderlinedDigits } from '../utils/paperDiff';
import { toPlainMath } from '../utils/plainMath';

function UnderlinedRun({ text, map, className }) {
  return splitUnderlinedDigits(text, map).map((bit, i) => {
    const cls = [className, bit.under ? 'pd-ul' : ''].filter(Boolean).join(' ');
    return <span key={i} className={cls || undefined}>{bit.text}</span>;
  });
}

function MarkedText({ parts, variant, map }) {
  return parts.map((part, i) => (
    <UnderlinedRun
      key={i}
      text={part.text}
      map={map}
      className={part.mark ? `pd-mark pd-mark--${variant}` : ''}
    />
  ));
}

function reviewParts(sourceText, changes) {
  const source = String(sourceText || '');
  const sorted = [...(changes || [])].sort((a, b) => a.start - b.start);
  const parts = [];
  let cursor = 0;
  sorted.forEach((change) => {
    if (typeof change.start !== 'number' || change.start < cursor) return;
    if (change.start > cursor) {
      parts.push({ type: 'text', text: source.slice(cursor, change.start), key: `t-${cursor}` });
    }
    parts.push({ type: 'change', change, key: `c-${change.id}` });
    cursor = change.end;
  });
  if (cursor < source.length) {
    parts.push({ type: 'text', text: source.slice(cursor), key: `t-${cursor}` });
  }
  return parts;
}

function MarkTable({ table }) {
  return (
    <table className="pd-marks">
      <caption>
        {table.title}
        {table.formula ? ` · ${table.formula}` : ''}
      </caption>
      <thead>
        <tr>
          <th>Question</th>
          <th>Marks</th>
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={`${row.label}-${row.marks}`}>
            <td>{row.label}</td>
            <td>{row.marks}</td>
          </tr>
        ))}
        {table.differs ? (
          <>
            <tr className="is-total">
              <td>Printed total</td>
              <td>{table.printed}</td>
            </tr>
            <tr className="is-total is-diff">
              <td>Counted total</td>
              <td>{table.counted}</td>
            </tr>
          </>
        ) : (
          <tr className="is-total">
            <td>Total</td>
            <td>{table.printed}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export default function PaperDiffReview({
  sourceText,
  changes,
  decisions,
  dropped,
  unmatched,
  tables,
  locked,
  onDecide,
  onEditCorrection
}) {
  const parts = reviewParts(sourceText, changes);
  const underlineMap = paperUnderlineMap(toPlainMath(sourceText));
  const chosen = decisions || {};
  const skipped = Array.isArray(unmatched) && unmatched.length ? unmatched : [];
  const skippedCount = skipped.length || (dropped > 0 ? dropped : 0);
  const [editId, setEditId] = React.useState(null);
  const [draft, setDraft] = React.useState('');

  const beginEdit = (change) => {
    setEditId(change.id);
    setDraft(toPlainMath(change.corrected || ''));
  };
  const cancelEdit = () => {
    setEditId(null);
    setDraft('');
  };
  const commitEdit = (change) => {
    if (onEditCorrection) onEditCorrection(change.id, draft);
    setEditId(null);
    setDraft('');
  };

  return (
    <div className="pd-review">
      {skippedCount > 0 && (
        <div className="pd-warn">
          {skippedCount} suggestion{skippedCount === 1 ? '' : 's'} quoted words that are not in the answer key, so {skippedCount === 1 ? 'it was' : 'they were'} skipped:
          {skipped.length > 0 ? (
            <ul className="pd-warn-list">
              {skipped.map((item, i) => {
                const quote = typeof item === 'string' ? item : (item.original || '');
                const fix = typeof item === 'string' ? '' : (item.corrected || '');
                const why = typeof item === 'string' ? '' : (item.reason || '');
                return (
                  <li key={`${i}-${quote}`}>
                    {quote ? <span>“{quote}”</span> : <span>No quote was given.</span>}
                    {fix ? <span> → “{fix}”</span> : null}
                    {why ? <span> ({why})</span> : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div>The skipped wording was not included in the model reply.</div>
          )}
        </div>
      )}
      {(tables || []).map((table) => (
        <MarkTable key={table.title} table={table} />
      ))}
      <div className="pd-paper">
        {parts.map((part) => {
          if (part.type === 'text') {
            if (!part.text) return null;
            return (
              <pre key={part.key} className="pd-plain">
                <UnderlinedRun text={toPlainMath(part.text)} map={underlineMap} />
              </pre>
            );
          }
          const { change } = part;
          const decision = chosen[change.id] || '';
          const editing = editId === change.id;
          const spans = inlineChangeSpans(
            toPlainMath(change.original),
            editing ? draft : toPlainMath(change.corrected)
          );
          return (
            <div key={part.key} className={`pd-hunk${decision ? ` pd-hunk--${decision}` : ''}`}>
              <div className="pd-hunk-body">
                <pre className="pd-line pd-line--old"><MarkedText parts={spans.old} variant="old" map={underlineMap} /></pre>
                {editing ? (
                  <textarea
                    className="pd-line pd-line--new pd-line-edit"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    spellCheck={false}
                    autoFocus
                    aria-label="Edit correction"
                  />
                ) : (
                  <pre className="pd-line pd-line--new"><MarkedText parts={spans.neu} variant="new" map={underlineMap} /></pre>
                )}
                {change.reason ? <div className="pd-reason">{change.reason}</div> : null}
              </div>
              <div className="pd-hunk-actions">
                {editing ? (
                  <>
                    <button
                      type="button"
                      className="pd-choice pd-choice--accept is-on"
                      aria-label="Save correction edit"
                      title="Save correction edit"
                      onClick={() => commitEdit(change)}
                    >
                      <i className="fas fa-check" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="pd-choice"
                      aria-label="Cancel correction edit"
                      title="Cancel correction edit"
                      onClick={cancelEdit}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </>
                ) : (
                  <>
                    {!locked && (
                      <button
                        type="button"
                        className="pd-choice"
                        aria-label="Edit correction"
                        title="Edit correction"
                        onClick={() => beginEdit(change)}
                      >
                        <i className="fas fa-pen" aria-hidden="true"></i>
                      </button>
                    )}
                    <button
                      type="button"
                      className={`pd-choice pd-choice--accept${decision === 'accept' ? ' is-on' : ''}`}
                      disabled={locked}
                      aria-pressed={decision === 'accept'}
                      aria-label="Accept correction"
                      title="Accept correction"
                      onClick={() => onDecide(change.id, 'accept')}
                    >
                      <i className="fas fa-check" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className={`pd-choice pd-choice--reject${decision === 'reject' ? ' is-on' : ''}`}
                      disabled={locked}
                      aria-pressed={decision === 'reject'}
                      aria-label="Keep original"
                      title="Keep original"
                      onClick={() => onDecide(change.id, 'reject')}
                    >
                      <i className="fas fa-times" aria-hidden="true"></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
